import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../lib/firebase';
import {
  sendPasswordResetEmail,
  confirmPasswordReset,
  applyActionCode,
} from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BRAND = '#214140';
const GREEN = '#1F8051';
const LIGHT = '#DFEAF0';
const inputCls = "w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent";

export default function DistributorLoginPage() {
  const navigate = useNavigate();
  const { signIn, user, profile } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot' | 'reset' | 'verify-success'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [newPw, setNewPw]       = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [oobCode, setOobCode]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [message, setMessage]   = useState('');
  const [error, setError]       = useState('');

  // Redirect already-logged-in users
  useEffect(() => {
    if (user && profile) {
      navigate(profile.access_level === 'admin' ? '/distributors/admin' : '/distributors/portal', { replace: true });
    }
  }, [user, profile, navigate]);

  // Handle Firebase email action link in URL (?mode=resetPassword&oobCode=...)
  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const urlMode = params.get('mode');
    const code    = params.get('oobCode');
    if (!code) return;

    if (urlMode === 'resetPassword') {
      setOobCode(code);
      setMode('reset');
    } else if (urlMode === 'verifyEmail') {
      applyActionCode(auth, code)
        .then(() => setMode('verify-success'))
        .catch(() => setError('Verification link is invalid or expired.'));
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { user: fbUser } = await signIn(email, password);
      if (!fbUser.emailVerified) {
        await auth.signOut();
        setError('Please verify your email first. Check your inbox for the verification link.');
        setLoading(false);
        return;
      }
      // AuthContext will pick up the state change and redirect via useEffect above
    } catch (err) {
      const msg = err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
        ? 'Incorrect email or password.'
        : err.code === 'auth/user-not-found'
        ? 'No account found with that email.'
        : err.message;
      setError(msg);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin + '/distributors/login'
      });
      setMessage('Password reset email sent. Check your inbox.');
    } catch (err) {
      setError(err.code === 'auth/user-not-found'
        ? 'No account found with that email.'
        : err.message);
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw !== confirmPw) { setError('Passwords do not match.'); return; }
    if (newPw.length < 8)    { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPw);
      setMessage('Password updated! You can now sign in.');
      setTimeout(() => {
        setMode('login');
        window.history.replaceState({}, '', '/distributors/login');
      }, 2000);
    } catch (err) {
      setError('Reset link is invalid or expired. Request a new one.');
    }
    setLoading(false);
  };

  const titles = {
    login: 'Partner Sign In',
    forgot: 'Reset Password',
    reset: 'Set New Password',
    'verify-success': 'Email Verified',
  };
  const subtitles = {
    login: 'Cordflex Distributor Portal',
    forgot: "We'll email you a reset link",
    reset: 'Choose a new secure password',
    'verify-success': 'Your account is confirmed',
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-sm">

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
              style={{ background: BRAND }}>
              <span className="text-white text-2xl">
                {mode === 'verify-success' ? '✓' : '🔒'}
              </span>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: BRAND }}>{titles[mode]}</h1>
            <p className="text-sm text-gray-500 mt-1">{subtitles[mode]}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-7" style={{ border: '1px solid ' + LIGHT }}>

            {/* ── EMAIL VERIFIED SUCCESS ── */}
            {mode === 'verify-success' && (
              <div className="text-center space-y-4">
                <p className="text-sm text-gray-600">Your email has been verified. You can now sign in to access the portal.</p>
                <button onClick={() => setMode('login')}
                  className="w-full py-3 rounded-xl font-semibold text-white hover:opacity-90"
                  style={{ background: BRAND }}>
                  Sign In
                </button>
              </div>
            )}

            {/* ── LOGIN ── */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: BRAND }}>Email</label>
                  <input type="email" className={inputCls} value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-sm font-semibold" style={{ color: BRAND }}>Password</label>
                    <button type="button" className="text-xs hover:underline" style={{ color: GREEN }}
                      onClick={() => { setMode('forgot'); setError(''); setMessage(''); }}>
                      Forgot password?
                    </button>
                  </div>
                  <input type="password" className={inputCls} value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
                {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: BRAND }}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            )}

            {/* ── FORGOT PASSWORD ── */}
            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: BRAND }}>Your Email</label>
                  <input type="email" className={inputCls} value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
                </div>
                {error   && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                {message && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{message}</div>}
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: BRAND }}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" className="w-full text-sm text-center hover:underline" style={{ color: GREEN }}
                  onClick={() => { setMode('login'); setError(''); setMessage(''); }}>
                  Back to sign in
                </button>
              </form>
            )}

            {/* ── RESET PASSWORD ── */}
            {mode === 'reset' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: BRAND }}>New Password</label>
                  <input type="password" className={inputCls} placeholder="Min. 8 characters"
                    value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8} autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: BRAND }}>Confirm New Password</label>
                  <input type="password" className={inputCls}
                    value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required />
                </div>
                {error   && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                {message && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{message}</div>}
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: BRAND }}>
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            Not a partner yet?{' '}
            <Link to="/distributors/apply" className="font-semibold hover:underline" style={{ color: BRAND }}>Apply here</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
